import React, { ChangeEvent, ChangeEventHandler, useId, useMemo } from "react";

import { Dialog } from "../../../../../dialog";
import {
  CharacterCount,
  ChoiceCheckbox,
  ChoiceImage,
  ChoiceRadio,
  Container,
  PromptContent,
  StyledChoiseGrid,
  TextArea,
  DialogHeader,
  DialogContent,
} from "./styles";
import { Grid } from "../../../../../Grid";
import { RadioGroup } from "../../../../../RadioGroup";
import { Survey } from "./Survey";
import { GradedQuiz } from "./GradedQuiz";
import { Ungraded } from "./Ungraded";

type ButtonColor = "primary" | "positive" | "negative";

export type Choice = {
  id: string;
  type: "TEXT" | "IMAGE" | "INPUT";
  content?: string;
  mediaUrl?: string;
  isCorrect?: boolean;
  isChecked?: boolean;
  isFailed?: boolean;
};

export type Prompt = {
  id: string;
  content: string;
  choices: Choice[];
  isCompleted: boolean | null;
  isMultiple?: boolean;
};

type FormModalProps = {
  isOpen: boolean;
  isReview: boolean;
  promptIndex: number;
  promptCount: number;
  prompt: Prompt;
  choices: Choice[];
  promptIsCorrect: boolean;
  selectedChoiceIds: Array<string>;
  submittedChoiceIds: Array<string>;
  buttonDisabled: boolean;
  hasInputChoice: boolean;
  input: string;
  isSurvey: boolean;
  isGradedQuiz: boolean;
  isFormSubmitting: boolean;
  continueStr: string;
  incorrectStr: string;
  correctStr: string;
  areChoicesDisabled: boolean;
  checkAnswerStr: string;
  tryAgainStr: string;
  onCloseForm: () => void;
  onSubmit: VoidFunction;
  onChangeChoice: (
    e: ChangeEvent<HTMLInputElement>,
    value: string,
    isMultiple?: boolean
  ) => void;
  onChangeInput: ChangeEventHandler<HTMLTextAreaElement>;
};

type DerivedDisplayState = {
  buttonText: string;
  buttonColor: ButtonColor;
  actionsBackgroundColor?: "positive" | "negative";
  resultText?: string;
};

export function FormModal({
  isOpen,
  isReview,
  promptIndex,
  promptCount,
  prompt,
  choices,
  selectedChoiceIds,
  submittedChoiceIds,
  areChoicesDisabled,
  buttonDisabled,
  hasInputChoice,
  input,
  isSurvey,
  isGradedQuiz,
  isFormSubmitting,
  continueStr,
  promptIsCorrect,
  incorrectStr,
  correctStr,
  checkAnswerStr,
  tryAgainStr,
  onCloseForm,
  onSubmit,
  onChangeChoice,
  onChangeInput,
}: FormModalProps) {
  const modalId = useId();

  const filteredChoices = useMemo(() => {
    return choices.filter((choice) => choice.type !== "INPUT");
  }, [choices]);

  const formDisplayState = useMemo<DerivedDisplayState>(() => {
    if (isFormSubmitting) {
      return {
        buttonText: "",
        buttonColor: "primary",
        actionsBackgroundColor: undefined,
        resultText: undefined,
      };
    }

    const hasSubmitted = submittedChoiceIds && submittedChoiceIds.length > 0;

    if (!hasSubmitted) {
      if (isSurvey) {
        return {
          buttonText: continueStr,
          buttonColor: "primary",
          actionsBackgroundColor: undefined,
          resultText: undefined,
        };
      }

      return {
        buttonText: checkAnswerStr || "Check Answer",
        buttonColor: "primary",
        actionsBackgroundColor: undefined,
        resultText: undefined,
      };
    }

    if (isSurvey) {
      return {
        buttonText: continueStr,
        buttonColor: "positive",
        actionsBackgroundColor: "positive",
        resultText: undefined,
      };
    }

    if (promptIsCorrect) {
      return {
        buttonText: continueStr,
        buttonColor: "positive",
        actionsBackgroundColor: "positive",
        resultText: correctStr,
      };
    } else {
      if (isGradedQuiz) {
        return {
          buttonText: continueStr,
          buttonColor: "negative",
          actionsBackgroundColor: "negative",
          resultText: incorrectStr,
        };
      } else {
        return {
          buttonText: tryAgainStr,
          buttonColor: "negative",
          actionsBackgroundColor: "negative",
          resultText: incorrectStr,
        };
      }
    }
  }, [
    isFormSubmitting,
    submittedChoiceIds,
    isSurvey,
    isGradedQuiz,
    promptIsCorrect,
    continueStr,
    correctStr,
    incorrectStr,
    checkAnswerStr,
    tryAgainStr,
  ]);

  return (
    <Dialog
      open={isOpen}
      fullScreen
      aria-labelledby={modalId}
      onClose={onCloseForm}
    >
      <DialogHeader isCenter id={modalId} onClose={onCloseForm}>
        {promptIndex + 1}/{promptCount}
      </DialogHeader>
      <DialogContent dividers={false}>
        <Container>
          <PromptContent>{prompt.content}</PromptContent>
          {prompt?.isMultiple ? (
            <Grid>
              {filteredChoices.map((choice) => {
                const isImage = choice.type === "IMAGE";

                return (
                  <StyledChoiseGrid
                    key={`${choice?.id} ${choice.content}`}
                    xs="auto"
                    $isReview={
                      isReview && (choice.isChecked || choice.isCorrect)
                    }
                  >
                    <ChoiceCheckbox
                      key={choice.id}
                      checked={selectedChoiceIds.includes(choice.id)}
                      label={
                        isImage ? (
                          <ChoiceImage
                            src={choice.mediaUrl}
                            alt={choice.content}
                          />
                        ) : (
                          choice.content
                        )
                      }
                      onChange={(e) =>
                        onChangeChoice(e, choice.id, !!prompt?.isMultiple)
                      }
                      disabled={isReview || areChoicesDisabled}
                      $isImage={isImage}
                    />
                  </StyledChoiseGrid>
                );
              })}
            </Grid>
          ) : (
            <RadioGroup
              value={
                (selectedChoiceIds?.[0] || submittedChoiceIds?.[0]) ?? null
              }
              onChange={(e, value) =>
                onChangeChoice(e, value, !!prompt?.isMultiple)
              }
            >
              {filteredChoices.map((choice) => {
                const isImage = choice.type === "IMAGE";

                return (
                  <ChoiceRadio
                    key={choice.id}
                    value={choice.id}
                    label={
                      isImage ? (
                        <ChoiceImage
                          src={choice.mediaUrl}
                          alt={choice.content}
                        />
                      ) : (
                        choice.content
                      )
                    }
                    disabled={isReview || areChoicesDisabled}
                    $isImage={isImage}
                    $isReview={isReview && choice.isCorrect}
                  />
                );
              })}
            </RadioGroup>
          )}

          {hasInputChoice && (
            <>
              <TextArea
                autoFocus
                $state={input.length > 1000 ? "limit" : "valid"}
                value={input}
                disabled={areChoicesDisabled}
                onChange={onChangeInput}
              />
              <CharacterCount $state={input.length > 1000 ? "limit" : "valid"}>
                {input.length}/1000
              </CharacterCount>
            </>
          )}
        </Container>
      </DialogContent>

      {isSurvey ? (
        <Survey
          isLoading={isFormSubmitting}
          onSubmit={onSubmit}
          buttonText={formDisplayState.buttonText}
        />
      ) : isGradedQuiz ? (
        <GradedQuiz
          isLoading={isFormSubmitting}
          onSubmit={onSubmit}
          buttonText={formDisplayState.buttonText}
          color={formDisplayState.buttonColor}
          backgroundColor={formDisplayState.actionsBackgroundColor}
          resultText={formDisplayState.resultText}
          isButtonDisabled={buttonDisabled}
        />
      ) : (
        <Ungraded
          isLoading={isFormSubmitting}
          onSubmit={onSubmit}
          buttonText={formDisplayState.buttonText}
          color={formDisplayState.buttonColor}
          isButtonDisabled={buttonDisabled}
          backgroundColor={formDisplayState.actionsBackgroundColor}
          resultText={formDisplayState.resultText}
          isReview={isReview}
        />
      )}
    </Dialog>
  );
}
